import styled from "@emotion/styled";
import { Skeleton } from "antd";
import { ReactNode } from "react";
import { sd } from "../tokens";
import IconChip from "../shared/IconChip";

interface KpiCardProps {
  label: string;
  value: ReactNode;
  /** หน่วยต่อท้ายตัวเลข เช่น "รายการ" */
  unit?: string;
  chipIcon: ReactNode;
  chipBg: string;
  chipColor: string;
  /** แถบสีส้มด้านซ้าย (การ์ดใบแรก) */
  accent?: boolean;
  /** ↗ 18.4% จากช่วงก่อนหน้า */
  delta?: ReactNode;
  linkText?: string;
  onLinkClick?: () => void;
  loading?: boolean;
  refreshing?: boolean;
  error?: boolean;
}

const KpiCard = ({
  label,
  value,
  unit,
  chipIcon,
  chipBg,
  chipColor,
  accent,
  delta,
  linkText,
  onLinkClick,
  loading,
  refreshing,
  error,
}: KpiCardProps) => (
  <Card accent={!!accent}>
    <Head>
      <Label>{label}</Label>
      <IconChip icon={chipIcon} bg={chipBg} color={chipColor} />
    </Head>
    {loading ? (
      <Skeleton active title={{ width: "55%" }} paragraph={{ rows: 1, width: "75%" }} />
    ) : error ? (
      <ErrorText>ไม่สามารถโหลดข้อมูลได้</ErrorText>
    ) : (
      <Body refreshing={!!refreshing}>
        <ValueRow>
          <Value>{value}</Value>
          {unit ? <Unit>{unit}</Unit> : null}
        </ValueRow>
        <Foot>
          {delta}
          {linkText ? (
            <LinkButton type="button" onClick={onLinkClick}>
              {linkText} →
            </LinkButton>
          ) : null}
        </Foot>
      </Body>
    )}
  </Card>
);

export default KpiCard;

const Card = styled.div<{ accent: boolean }>`
  position: relative;
  overflow: hidden;
  background: ${sd.cardBg};
  border: 1px solid ${sd.border};
  border-radius: ${sd.radius}px;
  box-shadow: ${sd.shadow};
  padding: 18px 20px;
  height: 100%;
  min-height: 132px;
  display: flex;
  flex-direction: column;

  ${(p) =>
    p.accent &&
    `
    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background: ${sd.orange};
    }
  `}
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
`;

const Label = styled.div`
  font-size: 13px;
  color: ${sd.muted};
`;

const Body = styled.div<{ refreshing: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: opacity 0.15s ease;
  opacity: ${(p) => (p.refreshing ? 0.55 : 1)};
`;

const ValueRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
`;

const Value = styled.div`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
  color: ${sd.navy};
  word-break: break-all;
`;

const Unit = styled.span`
  font-size: 13px;
  color: ${sd.muted};
`;

const Foot = styled.div`
  margin-top: 10px;
  min-height: 20px;
`;

const LinkButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: ${sd.blue};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ErrorText = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  font-size: 13px;
  color: ${sd.muted};
`;
